/**
 * Partners API endpoints.
 */

import { apiClient } from './api-client';
import type {
  Partner,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  ListPartnersParams,
  PartnerOption,
} from '@/types/partner.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Partners API prefix (`/partners/partners`). */
const PARTNERS_BASE = '/partners';

/** `GET /partners/partners` — paginated partners. */
export const listPartners = async (
  params?: ListPartnersParams,
): Promise<PaginatedResponse<Partner>> => {
  const response = await apiClient.get<PaginatedResponse<Partner>>(`${PARTNERS_BASE}/partners`, {
    params,
  });
  return response.data;
};

/** `GET /partners/partners/:id` */
export const getPartnerById = async (id: string): Promise<Partner> => {
  const response = await apiClient.get<Partner>(`${PARTNERS_BASE}/partners/${id}`);
  return response.data;
};

/** `POST /partners/partners` */
export const createPartner = async (data: CreatePartnerRequest): Promise<Partner> => {
  const response = await apiClient.post<Partner>(`${PARTNERS_BASE}/partners`, data);
  return response.data;
};

/** `PUT /partners/partners/:id` */
export const updatePartner = async (id: string, data: UpdatePartnerRequest): Promise<Partner> => {
  const response = await apiClient.put<Partner>(`${PARTNERS_BASE}/partners/${id}`, data);
  return response.data;
};

/** `DELETE /partners/partners/:id` */
export const deletePartner = async (id: string): Promise<void> => {
  await apiClient.delete(`${PARTNERS_BASE}/partners/${id}`);
};

/** `GET /partners/partners/export-excel` — download XLSX. */
export const exportPartnersExcel = async (params?: ListPartnersParams): Promise<void> => {
  const response = await apiClient.get(`${PARTNERS_BASE}/partners/export-excel`, {
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `partenaires-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** `GET /partners` — slim list for selects. */
export const getPartnerOptions = async (): Promise<PartnerOption[]> => {
  const { data } = await apiClient.get<PartnerOption[]>('/partners');
  return data;
};
